import { useEffect, useState } from 'react';
import { useAnalyticsStore } from '~contexts/mobx';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from '~containers/analytics/kpis/sources/road-to-rewards-campaign';
import type { KpiSegment } from '~containers/analytics/kpis/types';
import type { DegradationCause } from '~stores/kpi-envelope';

/** One milestone's row on the `visitMilestone` axis: the two denominators the
 * bar draws on, never a share (the endpoint serves counts only). */
export interface MilestoneReachRow {
    reachedMembers: number;
    choosingMembers: number;
}

/** One reward option on the `rewardOption` axis, named by the endpoint so a
 * removed option still reads under its own name. */
export interface MilestoneOptionRow {
    label: string;
    choices: number;
}

export type CampaignMilestoneChoicesState =
    | { status: 'loading' }
    | {
          status: 'ready';
          /** Keyed by the axis's own milestone key (the cumulative visit
           * count as a string). Absent = the endpoint did not serve it. */
          reachByMilestone: Map<string, MilestoneReachRow>;
          /** Keyed like `reachByMilestone`, in the order the axis serves. */
          optionsByMilestone: Map<string, MilestoneOptionRow[]>;
      }
    | { status: 'unavailable'; reason: string; cause?: DegradationCause };

const CHOICES_FAILED =
    'The milestone choice distribution could not be loaded for this customer group.';

/** A row cell is contractually `number | string | null`; a missing or
 * non-numeric cell reads as 0, matching `create-envelope-source.ts`'s own
 * `finiteCell`. */
const finiteCell = (cell: unknown): number => {
    const n = Number(cell ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/**
 * The `campaign-milestone-choices-member` endpoint's two axes, for the
 * customer group the block's selector holds.
 *
 * Sends no `startDate`/`endDate`: the endpoint is a standing read (the ladder
 * as of today) and ignores them, so leaving them out is what keeps the page's
 * Period selector out of this request's identity, rather than a client-side
 * rule that a later edit could drop.
 *
 * Bypasses the `KpiSource`/card pipeline, which reads one metric per card,
 * where a milestone panel needs both axes at once. It does take the pipeline's
 * abandonment protocol with it: the fetch carries an `AbortSignal` and a
 * resolution for a group the reader has left is discarded, so a segment change
 * mid-flight cannot land the previous group's figures (RNGR-2381 defect F).
 */
export const useCampaignMilestoneChoices = (
    segment: KpiSegment,
    provinces: string[]
): CampaignMilestoneChoicesState => {
    const analyticsStore = useAnalyticsStore();
    const [state, setState] = useState<CampaignMilestoneChoicesState>({
        status: 'loading',
    });

    const provincesKey = provinces.join(',');

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        setState({ status: 'loading' });

        const load = async () => {
            try {
                const params: Record<string, string> = {
                    campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID,
                    segment,
                };
                if (provincesKey.length > 0) {
                    params.provinces = provincesKey;
                }
                const env = await analyticsStore.fetchKpiEnvelope(
                    'analytics/campaign-milestone-choices-member',
                    params,
                    controller.signal
                );
                if (cancelled) return;
                if (!env) {
                    setState({ status: 'unavailable', reason: CHOICES_FAILED });
                    return;
                }
                if (env.degradation) {
                    setState({
                        status: 'unavailable',
                        reason: env.degradation.reason,
                        ...(env.degradation.cause !== undefined && {
                            cause: env.degradation.cause,
                        }),
                    });
                    return;
                }

                const reachByMilestone = new Map<string, MilestoneReachRow>();
                const milestoneAxis = env.breakdowns.find(
                    (breakdown) => breakdown.by === 'visitMilestone'
                );
                for (const row of milestoneAxis?.rows ?? []) {
                    reachByMilestone.set(row.key, {
                        reachedMembers: finiteCell(row.reachedMembers),
                        choosingMembers: finiteCell(row.choosingMembers),
                    });
                }

                const optionsByMilestone = new Map<
                    string,
                    MilestoneOptionRow[]
                >();
                const optionAxis = env.breakdowns.find(
                    (breakdown) => breakdown.by === 'rewardOption'
                );
                for (const row of optionAxis?.rows ?? []) {
                    const milestone = String(row.milestone);
                    optionsByMilestone.set(milestone, [
                        ...(optionsByMilestone.get(milestone) ?? []),
                        { label: row.label, choices: finiteCell(row.choices) },
                    ]);
                }

                setState({
                    status: 'ready',
                    reachByMilestone,
                    optionsByMilestone,
                });
            } catch {
                if (!cancelled) {
                    setState({ status: 'unavailable', reason: CHOICES_FAILED });
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [analyticsStore, segment, provincesKey]);

    return state;
};
